// ============= Imports =============
import { UserRelayHubEvents } from './events';
import type {
  UserEventParsed,
  UserRelayEventHandler,
  UserRelayPresenceMessage,
  UserRelayUserPayload,
  UserRelayWorkspacePayload,
  UserWorkspaceEventParsed,
} from './types';

// ============= Type guards =============

export const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const getStringField = (record: unknown, key: string): string | undefined => {
  if (!isRecord(record)) {
    return undefined;
  }
  const value = record[key];
  return typeof value === 'string' ? value : undefined;
};

export const isUserRelayUserPayload = (
  value: unknown,
): value is UserRelayUserPayload => isRecord(value);

export const isUserRelayWorkspacePayload = (
  value: unknown,
): value is UserRelayWorkspacePayload => isRecord(value);

export const isUserRelayPresenceMessage = (
  value: unknown,
): value is UserRelayPresenceMessage => isRecord(value);

const getUserIdFromPayload = (payload: unknown): string | undefined =>
  getStringField(payload, 'userId') ||
  getStringField(payload, 'id') ||
  getStringField(payload, 'userEmail');

const getUserNameFromPayload = (payload: unknown, fallback: string): string =>
  getStringField(payload, 'name') ||
  getStringField(payload, 'userName') ||
  getStringField(payload, 'userEmail') ||
  fallback;

const getWorkspaceNameFromPayload = (payload: unknown): string =>
  getStringField(payload, 'name') ||
  getStringField(payload, 'title') ||
  getStringField(payload, 'workspaceName') ||
  'Workspace';

const resolveWorkspaceId = (
  param1: unknown,
  workspaceIdParam?: string,
): string => {
  if (workspaceIdParam) {
    return workspaceIdParam;
  }
  if (typeof param1 === 'string') {
    return param1;
  }
  return getStringField(param1, 'id') || 'unknown';
};

// ============= Types =============

export interface UserEventHandlerContext {
  workspaceId?: string;
  log: (
    event: string,
    data: unknown,
    meta?: { workspaceId?: string; displayName?: string },
  ) => void;
  onEventReceived?: (
    event: string,
    data: unknown,
    parsed?: Record<string, unknown>,
  ) => void;
  onMessageReceived?: (message: unknown) => void;
  onDataRefresh?: () => void | Promise<void>;
  enableToasts: boolean;
  showSuccess: (message: string, duration?: number) => void;
  showError: (message: string, duration?: number) => void;
  showInfo: (message: string, duration?: number) => void;
  showWarning: (message: string, duration?: number) => void;
}

export interface UserPresenceEventArgs {
  workspaceIdParam?: string;
  userIdParam: string;
  userData?: unknown;
}

export interface UserReceiveMessageArgs {
  senderId?: string;
  message: UserRelayPresenceMessage;
}

/** Normalized result of (param1, workspaceIdParam?, userData?) for user events. */
export const normalizeUserEventArgs = (
  param1: unknown,
  workspaceIdParam?: string,
  userData?: unknown,
): {
  actualWorkspaceId: string;
  actualUserId: string;
  actualUserData: unknown;
  userName: string;
  parsed: UserEventParsed;
} => {
  let actualWorkspaceId: string;
  let actualUserId: string;
  let actualUserData: unknown;

  if (userData === undefined && typeof workspaceIdParam === 'string') {
    actualWorkspaceId =
      typeof param1 === 'string' ? param1 : resolveWorkspaceId(param1);
    actualUserData = workspaceIdParam;
    actualUserId = getUserIdFromPayload(actualUserData) || 'unknown';
  } else {
    actualWorkspaceId = resolveWorkspaceId(param1, workspaceIdParam);
    actualUserData = userData ?? param1;
    actualUserId =
      getUserIdFromPayload(actualUserData) ||
      (typeof param1 === 'string' ? param1 : 'unknown');
  }

  const userName = getUserNameFromPayload(actualUserData, actualUserId);

  const parsed: UserEventParsed = {
    workspaceId: actualWorkspaceId,
    userId: actualUserId,
    userData: actualUserData,
    userName,
    param1,
  };

  return { actualWorkspaceId, actualUserId, actualUserData, userName, parsed };
};

/** Normalized result of (param1, workspaceIdParam?, workspaceData?) for workspace events. */
export const normalizeWorkspaceEventArgs = (
  param1: unknown,
  workspaceIdParam?: string,
  workspaceData?: unknown,
): {
  actualWorkspaceId: string;
  actualWorkspaceData: unknown;
  workspaceName: string;
  parsed: UserWorkspaceEventParsed;
} => {
  let actualWorkspaceId: string;
  let actualWorkspaceData: unknown;

  if (workspaceData === undefined && typeof workspaceIdParam === 'string') {
    actualWorkspaceId =
      typeof param1 === 'string' ? param1 : resolveWorkspaceId(param1);
    actualWorkspaceData = workspaceIdParam;
  } else {
    actualWorkspaceId = resolveWorkspaceId(param1, workspaceIdParam);
    actualWorkspaceData = workspaceData ?? param1;
  }

  const workspaceName = getWorkspaceNameFromPayload(actualWorkspaceData);

  const parsed: UserWorkspaceEventParsed = {
    workspaceId: actualWorkspaceId,
    workspaceData: actualWorkspaceData,
    workspaceName,
    param1,
  };

  return { actualWorkspaceId, actualWorkspaceData, workspaceName, parsed };
};

/**
 * Parses USER_ONLINE / USER_OFFLINE args: (userId) | (workspaceId, userId) | (workspaceId, userId, userData).
 */
export const parseUserPresenceArgs = (
  args: unknown[],
): UserPresenceEventArgs => {
  const workspaceIdParam =
    args.length >= 2 && typeof args[0] === 'string' ? args[0] : undefined;
  const userIdParam =
    args.length >= 2 && typeof args[1] === 'string'
      ? args[1]
      : args.length >= 1 && typeof args[0] === 'string'
        ? args[0]
        : 'unknown';
  const userData =
    args.length >= 3
      ? args[2]
      : args.length === 2 &&
          typeof args[1] === 'object' &&
          args[1] !== null
        ? args[1]
        : undefined;

  return { workspaceIdParam, userIdParam, userData };
};

/** Parses ReceiveMessage(senderId, message) args from the user relay hub. */
export const parseUserReceiveMessageArgs = (
  args: unknown[],
): UserReceiveMessageArgs => {
  const senderId =
    args.length >= 1 && typeof args[0] === 'string' ? args[0] : undefined;
  const rawMessage = args.length >= 2 ? args[1] : args[0];
  const message: UserRelayPresenceMessage = isUserRelayPresenceMessage(
    rawMessage,
  )
    ? rawMessage
    : { type: undefined };

  return { senderId, message };
};

export interface UserEventConfig {
  event: UserRelayHubEvents;
  toastType: 'success' | 'error' | 'info';
  getToastMessage: (parsed: Pick<UserEventParsed, 'userName'>) => string;
}

/**
 * Creates a handler for user relay hub events that use (param1, workspaceIdParam?, userData?).
 */
export const createUserEventHandler = (
  ctx: UserEventHandlerContext,
  config: UserEventConfig,
): UserRelayEventHandler => {
  const { event, toastType, getToastMessage } = config;

  return (param1, workspaceIdParam, userData) => {
    const { actualWorkspaceId, parsed } = normalizeUserEventArgs(
      param1,
      workspaceIdParam,
      userData,
    );

    if (ctx.workspaceId && actualWorkspaceId !== ctx.workspaceId) return;

    ctx.log(
      event,
      { workspaceIdParam, ...parsed },
      { workspaceId: actualWorkspaceId, displayName: parsed.userName },
    );

    if (ctx.onEventReceived) {
      ctx.onEventReceived(
        event,
        {
          param1,
          workspaceId: actualWorkspaceId,
          userId: parsed.userId,
          userData: parsed.userData,
        },
        { ...parsed },
      );
    }

    if (ctx.onMessageReceived) {
      ctx.onMessageReceived({
        event,
        workspaceId: actualWorkspaceId,
        userId: parsed.userId,
        userData: parsed.userData,
        parsed,
      });
    }

    if (ctx.enableToasts) {
      const message = getToastMessage(parsed);
      if (toastType === 'success') ctx.showSuccess(message);
      else if (toastType === 'error') ctx.showError(message);
      else ctx.showInfo(message);
    }

    if (ctx.onDataRefresh) void ctx.onDataRefresh();
  };
};
